import React, { useEffect, useState } from "react";
import { client, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Database, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Backup() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.owner) return;
    client.get("/backup/summary").then(r => setSummary(r.data)).catch(() => setSummary(null));
  }, [user]);

  if (!user?.owner) {
    return (
      <div data-testid="backup-owner-only" className="bf-card mt-8" style={{ padding: "20px" }}>
        <div className="font-medium text-[15px]">Owner only</div>
        <div className="text-[13.5px] mt-1" style={{ color: "#5B6472" }}>
          Only the account owner can export a full copy of the register.
        </div>
      </div>
    );
  }

  const download = async () => {
    setBusy(true);
    try {
      const r = await client.get("/backup/download", { params: { confirm: "yes" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `blueflames-backup-${new Date().toISOString().slice(0, 10)}.ndjson`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded. Keep it somewhere you control.");
    } catch {
      toast.error("Backup failed. Nothing was written.");
    } finally {
      setBusy(false);
    }
  };

  const rows = Object.entries(summary?.collections || {}).filter(([, n]) => n > 0);

  return (
    <div data-testid="backup-page">
      <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Owner</div>
      <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>Backup</h1>
      <p className="text-[13.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5, maxWidth: "62ch" }}>
        One file holding your whole register — customers, sites, areas, equipment, jobs with their
        checklists and frozen reports, parts, settings and the audit log. Plain text, one record per
        line, readable without this app. Keep it somewhere you control.
      </p>

      <div className="mt-7 bf-card" style={{ padding: "18px" }}>
        <div className="flex items-center gap-4">
          <Database size={28} color="#2563EB" />
          <div className="flex-1">
            <div className="font-medium text-[15px]" data-testid="backup-record-count">
              {summary ? `${summary.records} records ready to export` : "Counting records…"}
            </div>
            <div className="text-[13px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
              Photographs and signatures live in file storage, not in this file. Every file record is
              included so a restore knows exactly which images to look for.
            </div>
          </div>
        </div>

        {rows.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="backup-collection-list">
            {rows.map(([name, n]) => (
              <span key={name} className="mono text-[11px] px-2 py-1 rounded-full"
                    style={{ background: "#EAF0FE", color: "#1E3A8A" }}>
                {name.replace(/_/g, " ")} {n}
              </span>
            ))}
          </div>
        )}

        <label className="mt-6 flex items-start gap-3 cursor-pointer" style={{ minHeight: "44px" }}>
          <Checkbox checked={confirmed} onCheckedChange={v => setConfirmed(!!v)}
                    data-testid="backup-confirm-checkbox" className="mt-1" />
          <span className="text-[13.5px]" style={{ color: "#12161B", lineHeight: 1.5 }}>
            I understand this file contains customer records and I will store it securely.
          </span>
        </label>

        <Button className="mt-4 h-11 px-5 bf-btn-primary" disabled={!confirmed || busy}
                onClick={download} data-testid="download-backup-btn">
          <Download size={16} className="mr-2" /> {busy ? "Preparing…" : "Download backup"}
        </Button>
      </div>

      <div className="mt-6 bf-card" style={{ padding: "18px" }}>
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} color="#136B36" />
          <div className="font-medium text-[15px]">Left out on purpose</div>
        </div>
        <ul className="mt-3 space-y-2" data-testid="backup-excluded-list">
          {(summary?.excluded || []).map(item => (
            <li key={item} className="text-[13.5px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>· {item}</li>
          ))}
        </ul>
        <div className="mt-4 text-[13px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
          Every download is recorded in the audit log with your name and the time. Restore steps are
          in <span className="mono">RESTORE_RUNBOOK.md</span>.
        </div>
      </div>
    </div>
  );
}
